/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import FiltroTermos from '../FiltroTermos';
import ExportButton from '../ExportButton';
import Tabela from '../Tabela';
import dayjs from 'dayjs';
import api from '../../../utils/api';

const TabTermos = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [filtro, setFiltro] = useState({
    pagina: 0,
    tamanhoPagina: 10,
    direcaoOrdenacao: 'DESC',
    campoOrdenacao: null,
    pesquisaRapida: null,
    numeroTermo: null,
    idsTiposTermo: null,
    municipio: null,
    dataInicio: null,
    dataFim: null
  });
  const [dados, setDados] = useState([]);
  const [total, setTotal] = useState(0);

  const downloadCSV = () => {
    const filtroCSV = {
      ...filtro,
      tamanhoPagina: 1000000
    };
    api.post(`/api/fiscalizacao/transparencia/csv/termo`, filtroCSV, {
      responseType: 'arraybuffer'
    }).then((response) => {
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'termos_transparencia.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }).catch((error) => {
      console.error('Erro ao baixar CSV:', error);
    });
  };

  const findTermos = async (param) => {
    try {
      const response = await api.post('/api/fiscalizacao/transparencia/termo/filtro', param);
      setDados(response.data.data.pageItems || []);
      setTotal(response.data.data.totalResults || 0);
    } catch (error) {
      console.error('Erro ao buscar termos:', error);
      setDados([]);
      setTotal(0);
    }
  };

  const findByFilter = async (param) => {
    try {
      await findTermos(param);
    } catch (error) {
      console.error('Erro ao aplicar filtro:', error);
    }
  };

  const form = useForm({
    initialValues: {
      pesquisaRapida: '',
      numeroTermo: '',
      idsTiposTermo: [],
      municipio: '',
      dataInicio: null,
      dataFim: null
    }
  });

  const clearFilter = () => {
    form.reset();
    setPagination({
      pageIndex: 0,
      pageSize: 10
    });
    setFiltro({
      pagina: 0,
      tamanhoPagina: 10,
      direcaoOrdenacao: 'DESC',
      campoOrdenacao: null,
      pesquisaRapida: null,
      numeroTermo: null,
      idsTiposTermo: null,
      municipio: null,
      dataInicio: null,
      dataFim: null
    });
    findTermos({
      pagina: 0,
      tamanhoPagina: 10,
      direcaoOrdenacao: 'DESC',
      campoOrdenacao: null,
      pesquisaRapida: null,
      numeroTermo: null,
      idsTiposTermo: null,
      municipio: null,
      dataInicio: null,
      dataFim: null
    });
  };

  const handleFilter = () => {
    const novoFiltro = {
      ...filtro,
      pagina: 0,
      pesquisaRapida: form.values.pesquisaRapida || null,
      numeroTermo: form.values.numeroTermo || null,
      idsTiposTermo: form.values.idsTiposTermo?.length > 0 ? form.values.idsTiposTermo : null,
      municipio: form.values.municipio || null,
      dataInicio: form.values.dataInicio
        ? dayjs(form.values.dataInicio).format('YYYY-MM-DD')
        : null,
      dataFim: form.values.dataFim
        ? dayjs(form.values.dataFim).format('YYYY-MM-DD')
        : null
    };
    setFiltro(novoFiltro);
    findTermos(novoFiltro);
  };

  const columnsTable = useMemo(
    () => [
      {
        accessorKey: 'codigo',
        header: 'Código',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'tipo.nome',
        header: 'Tipo Termo',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'tipoNaturezaEmbargo.descricao',
        header: 'Natureza',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'dataLavratura',
        header: 'Data Lavratura',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'areaTotalLocalizacao',
        header: 'Área (ha)',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        },
        Cell: ({ cell }) => {
          const valor = cell.getValue();
          return valor ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        }
      },
      {
        accessorKey: 'descricaoAcesso',
        header: 'Localização',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'municipio.nome',
        header: 'Município',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'municipio.estado.nome',
        header: 'Estado',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'pessoa.nome',
        header: 'Nome/Razão Social',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'pessoa.cpfCnpjFormatado',
        header: 'CPF/CNPJ',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'autoInfracao.codigo',
        header: 'Auto Infração',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'autoInfracao.areaTotalLocalizacao',
        header: 'Área Autuada (ha)',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        },
        Cell: ({ cell }) => {
          const valor = cell.getValue();
          return valor ? Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
        }
      },
      {
        accessorKey: 'autoInfracao.descricaoInfracao',
        header: 'Descrição Infração',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'justificativaCancelamento',
        header: 'Justificativa Cancelamento',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      },
      {
        accessorKey: 'setor.nome',
        header: 'Setor',
        mantineTableBodyCellProps: {
          align: 'left'
        },
        mantineTableHeadCellProps: {
          align: 'left'
        }
      }
    ],
    []
  );

  useEffect(() => {
    findTermos(filtro);
  }, []);

  useEffect(() => {
    if (
      pagination.pageIndex !== filtro.pagina ||
      pagination.pageSize !== filtro.tamanhoPagina
    ) {
      const novoFiltro = {
        ...filtro,
        pagina: pagination.pageIndex,
        tamanhoPagina: pagination.pageSize
      };
      setFiltro(novoFiltro);
      findByFilter(novoFiltro);
    }
  }, [pagination]);

  useEffect(() => {
    const novoFiltro = {
      ...filtro,
      pagina: 0,
      pesquisaRapida: form.values.pesquisaRapida || null,
      numeroTermo: form.values.numeroTermo || null,
      idsTiposTermo: form.values.idsTiposTermo?.length > 0 ? form.values.idsTiposTermo : null,
      municipio: form.values.municipio || null,
      dataInicio: form.values.dataInicio
        ? dayjs(form.values.dataInicio).format('YYYY-MM-DD')
        : null,
      dataFim: form.values.dataFim
        ? dayjs(form.values.dataFim).format('YYYY-MM-DD')
        : null
    };
    setFiltro(novoFiltro);
  }, [form.values]);

  return (
    <Stack gap={'0'}>
      <FiltroTermos
        clearFilter={clearFilter}
        form={form}
        handleFilter={handleFilter}
      />
      <ExportButton onExport={downloadCSV} />
      <Tabela
        columns={columnsTable}
        data={dados}
        rowCount={total}
        title={'Listagem de Termos emitidos (Fiscalização Online)'}
        setPagination={setPagination}
        state={{
          pagination: {
            pageIndex: filtro.pagina,
            pageSize: filtro.tamanhoPagina
          }
        }}
      />
    </Stack>
  );
};

export default TabTermos;
